import { EntryType } from '@morph-mapper/types';
import type { TreeNode } from '@morph-mapper/types';
import type { FieldSuggestion } from '../types/framework';
import { isPlaceholder } from '../utils/placeholders';
import {
  TRANSFORM_STAGES,
  type TransformContext,
  type TransformRenderFn,
  type TransformState,
  type TransformStep,
} from '../types/transform';

export const MAX_TRANSFORM_VARIANTS = 8;

export type TransformVariant = TransformState & {
  steps: string[];
  replacesInput: boolean;
};

const stageIndex = (step: TransformStep) => TRANSFORM_STAGES.indexOf(step.stage);

const sortSteps = (steps: TransformStep[]) =>
  steps
    .map((step, position) => ({ step, position }))
    .sort((a, b) => stageIndex(a.step) - stageIndex(b.step) || a.position - b.position)
    .map(({ step }) => step);

const applyStep = (
  variant: TransformVariant,
  step: TransformStep,
  ctx: TransformContext,
): TransformVariant[] => {
  const results = step.apply({ snippet: variant.snippet, value: variant.value }, ctx);
  if (results.length === 0) return [variant];

  const derived = results.map((result) => ({
    snippet: result.snippet,
    value: result.value,
    steps: [...variant.steps, step.name],
    replacesInput: variant.replacesInput && step.mode === 'replace',
  }));
  return step.mode === 'add' ? [variant, ...derived] : derived;
};

/**
 * Runs every step, in stage order, on the snippet of one suggestion. Each
 * step sees the snippet and value the previous step produced. Returns only
 * the variants at least one step changed.
 */
export const chainTransforms = (
  steps: TransformStep[],
  ctx: TransformContext,
): TransformVariant[] => {
  let frontier: TransformVariant[] = [
    {
      snippet: ctx.suggestion.snippet,
      value: ctx.suggestion.value,
      steps: [],
      replacesInput: true,
    },
  ];

  for (const step of sortSteps(steps)) {
    frontier = frontier
      .flatMap((variant) => applyStep(variant, step, ctx))
      .slice(0, MAX_TRANSFORM_VARIANTS + 1);
  }

  return frontier.filter((variant) => variant.steps.length > 0);
};

const COLUMN_VALUE = /^c\d+:\s*/i;

const normalize = (value: unknown) => String(value).replace(COLUMN_VALUE, '').trim();

export const rendersExpectedValue = (rendered: unknown, expected: unknown): boolean => {
  if (Array.isArray(rendered)) {
    return rendered.some((item) => rendersExpectedValue(item, expected));
  }
  if (rendered === undefined || rendered === null) return false;
  return normalize(rendered) === normalize(expected);
};

const EMPTY_RESULT = /^(NaN|Invalid date)$/i;

const isFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(isFilled);
  if (value === undefined || value === null) return false;
  const text = String(value).trim();
  return text !== '' && !EMPTY_RESULT.test(text);
};

/**
 * Under an iterator a snippet renders once per row, and each row has its own
 * value, so a transformed render can only be checked against the predicted
 * value in one row. The other rows must at least not be lost: a row the
 * original snippet filled must still be filled after the transform. A row
 * that only held a placeholder ("volgt", "N/A") may become empty.
 */
export const keepsRowsFilled = (original: unknown, transformed: unknown): boolean => {
  if (!Array.isArray(original) || !Array.isArray(transformed)) return true;
  return original.every(
    (row, i) => !isFilled(row) || isPlaceholder(row) || isFilled(transformed[i]),
  );
};

const isColumnValue = (value: unknown) => typeof value === 'string' && COLUMN_VALUE.test(value);

const verify = async (
  variant: TransformVariant,
  render: TransformRenderFn,
  originalRender: () => Promise<unknown>,
): Promise<boolean> => {
  if (isColumnValue(variant.value)) return true;
  const result = await render(JSON.stringify(variant.snippet));
  if (!result.isOk() || !rendersExpectedValue(result.value, variant.value)) return false;
  // Only a per-row render needs the original to compare against.
  return Array.isArray(result.value)
    ? keepsRowsFilled(await originalRender(), result.value)
    : true;
};

const valueKey = (value: unknown) =>
  typeof value === 'string' ? value.trim() : JSON.stringify(value);

// A column value ("c1: …") is passed without rendering (see verify), so only
// the others have been checked against a render.
const wasRendered = (variant: TransformVariant) => !isColumnValue(variant.value);

const toSuggestion = (base: FieldSuggestion, variant: TransformVariant): FieldSuggestion => ({
  ...base,
  value: variant.value,
  snippet: variant.snippet as FieldSuggestion['snippet'],
  type: EntryType.Code,
  source: [base.source, ...variant.steps].join(' + '),
  steps: variant.steps,
  ...(variant.replacesInput ? {} : { autofill: true }),
  // Its final snippet rendered to its value, so the sorting needn't render
  // it again; a wrapped snippet is an object it would otherwise score -50.
  ...(wasRendered(variant) ? { verified: true } : {}),
});

/**
 * A suggestion whose snippet is just its value, like an enum option: it
 * doesn't read anything from the document, so there is nothing to split,
 * clean or convert.
 */
const isLiteral = (suggestion: FieldSuggestion) =>
  typeof suggestion.snippet === 'string' && suggestion.snippet === suggestion.value;

/**
 * Suggestions the chain leaves as they are: iterator suggestions, literals,
 * and another field's value offered as a fallback. Transforming that last
 * one would make a checked suggestion out of a guess: an emptied "volgt"
 * from the delivery reference would top the client reference's list.
 */
const passesThrough = (suggestion: FieldSuggestion) =>
  suggestion.iterator === true || isLiteral(suggestion) || suggestion.crossField === true;

/**
 * The variants the steps make of one suggestion. Under an iterator the
 * snippet renders once per row: then the chain runs again knowing every row,
 * so a step can pick what fits all of them.
 */
const variantsFor = async (
  steps: TransformStep[],
  ctx: TransformContext,
  originalRender: () => Promise<unknown>,
): Promise<TransformVariant[]> => {
  const variants = chainTransforms(steps, ctx);
  if (variants.length === 0 || isColumnValue(ctx.suggestion.value)) return variants;
  const rendered = await originalRender();
  if (!Array.isArray(rendered)) return variants;
  return chainTransforms(steps, { ...ctx, rows: rendered.flat(Infinity) });
};

/**
 * Applies the transform steps of all enabled plugins to every suggestion.
 * A transformed suggestion is kept only when rendering its final snippet
 * gives the value the steps predicted. One render per variant, plus one of
 * the original snippet when there are variants.
 */
export const runTransformPipeline = async (args: {
  entry: TreeNode;
  suggestions: FieldSuggestion[];
  steps: TransformStep[];
  render: TransformRenderFn;
}): Promise<FieldSuggestion[]> => {
  const { entry, suggestions, steps, render } = args;
  if (steps.length === 0 || String(entry.key) === '#iterator') return suggestions;

  const kept = suggestions.filter(
    (suggestion) => !steps.some((step) => step.keep && !step.keep({ entry, suggestion, suggestions })),
  );

  const existing = new Set(kept.map((s) => valueKey(s.value)));
  // Variants that match what the plugin expected come before everything else.
  const preferred: FieldSuggestion[] = [];
  const output: FieldSuggestion[] = [];

  for (const suggestion of kept) {
    if (passesThrough(suggestion)) {
      output.push(suggestion);
      continue;
    }
    // Rendered at most once per suggestion, and only when a variant needs it.
    let original: Promise<unknown> | undefined;
    const originalRender = () =>
      (original ??= render(JSON.stringify(suggestion.snippet)).then(
        (result) => (result.isOk() ? result.value : undefined),
        () => undefined,
      ));

    const ctx = { entry, suggestion, suggestions: kept };
    const variants = await variantsFor(steps, ctx, originalRender);
    const result = await verifyVariants(suggestion, variants, existing, render, originalRender);
    preferred.push(...result.preferred);
    output.push(...result.rest);
  }

  return [...preferred, ...output];
};

const verifyVariants = async (
  suggestion: FieldSuggestion,
  variants: TransformVariant[],
  existing: Set<string>,
  render: TransformRenderFn,
  originalRender: () => Promise<unknown>,
): Promise<{ preferred: FieldSuggestion[]; rest: FieldSuggestion[] }> => {
  const verified: FieldSuggestion[] = [];
  let replaced = false;

  for (const variant of variants) {
    if (!variant.replacesInput && existing.has(valueKey(variant.value))) continue;
    if (!(await verify(variant, render, originalRender))) continue;
    existing.add(valueKey(variant.value));
    verified.push(toSuggestion(suggestion, variant));
    replaced ||= variant.replacesInput;
  }

  return replaced ? { preferred: [], rest: verified } : preferExpected(suggestion, verified);
};

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : null);

/**
 * A variant that is exactly what the plugin expected (e.g. what the LLM
 * read), while the original isn't, is the better value: it goes first and
 * the original no longer fills the field by itself.
 */
const preferExpected = (
  suggestion: FieldSuggestion,
  variants: FieldSuggestion[],
): { preferred: FieldSuggestion[]; rest: FieldSuggestion[] } => {
  const expected = text(suggestion.expectedValue);
  const isExpected = (s: FieldSuggestion) => expected !== null && text(s.value) === expected;
  const preferred = isExpected(suggestion) ? [] : variants.filter(isExpected);
  if (preferred.length === 0) return { preferred: [], rest: [suggestion, ...variants] };
  return {
    preferred,
    rest: [{ ...suggestion, autofill: false }, ...variants.filter((s) => !preferred.includes(s))],
  };
};
